import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Button, Heading, RadioCard, Stack, Text } from '@ubie/ubie-ui';
import Image from 'next/image';
import styles from './AppointmentServiceDescription.module.css';
import Link from 'next/link';
import PreRegistrationImage from '../../../../public/assets/img/appointment-pre-registration.svg';
import { getMedicalInstitutionAppointmentPreference } from '~/constants/medicalInstitutionAppointmentPreferences';
import { getDisplayAddress } from '~/models/medicalInstitution';
import { MedicalInstitutionForAppointmentFragment } from '../../../../gen/medinavi-backend-consumer-graphql/graphql';

type Props = {
  medicalInstitution: MedicalInstitutionForAppointmentFragment;
  onNext: (menuIndex: number) => void;
  medicoHospital: NonNullable<MedicalInstitutionForAppointmentFragment['medicoHospital']>;
};

export const AppointmentServiceDescription: FC<Props> = ({ medicalInstitution, onNext, medicoHospital }) => {
  const pref = getMedicalInstitutionAppointmentPreference(medicalInstitution);
  const isRequestAppointment = pref.type === 'requestAppointment';
  // メニューが1個しかない場合、自動でそのメニューを選択状態とする
  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number | null>(pref.menus.length === 1 ? 0 : null);
  const address = useMemo(() => getDisplayAddress(medicalInstitution), [medicalInstitution]);

  // 診療科
  const clinicalDepartmentNames = useMemo(() => {
    const deps =
      medicoHospital.clinicalDepartments.length > 0
        ? medicoHospital.clinicalDepartments
        : medicalInstitution.clinicalDepartments;
    return deps.map((dep) => dep.name);
  }, [medicalInstitution.clinicalDepartments, medicoHospital.clinicalDepartments]);

  // 次に進む処理
  const _onNext = useCallback(() => {
    if (selectedMenuIndex == null) return;
    onNext(selectedMenuIndex);
  }, [onNext, selectedMenuIndex]);

  // メニューの表示
  const renderMenus = useCallback(() => {
    // メニューが一個の場合、自動でそれを選択するので、メニューの表示はしない
    if (pref.menus.length === 1) return;

    const menuViews = pref.menus.map((menu, index) => {
      return (
        <RadioCard
          name="options"
          value={index}
          onChange={() => setSelectedMenuIndex(index)}
          checked={selectedMenuIndex === index}
          key={index}
        >
          {menu.name}
        </RadioCard>
      );
    });

    return (
      <Stack spacing="md" mt="xl" mb="md">
        <Text size="md" bold>
          受診内容の選択{' '}
          <Text size="sm" color="alert" as="span">
            ※必須
          </Text>
        </Text>
        {menuViews}
      </Stack>
    );
  }, [pref.menus, selectedMenuIndex]);

  if (pref.menus.length === 0) throw new Error('reservation pref menus is empty');

  return (
    <Box>
      <Box>
        <Stack alignItems="center" m="md">
          <Image src={PreRegistrationImage} alt="" priority width={181} height={140} />
        </Stack>

        {!isRequestAppointment && (
          <Stack spacing="md">
            <Heading size="md" textAlign="center">
              相談内容の事前登録に
              <br />
              ご協力ください
            </Heading>
            <Box>
              <Text>
                当サービスはスムーズな受付や診察につなげることを目的としています。 診察は
                <Text as="span" bold color="accent">
                  来院後の受付順
                </Text>
                です。混雑状況によっては
                <Text as="span" bold color="accent">
                  待ち時間が発生します。
                </Text>
              </Text>
            </Box>
          </Stack>
        )}

        {isRequestAppointment && (
          <Stack spacing="md">
            <Heading size="md" textAlign="center">
              ご利用にあたって
            </Heading>
            <Box>
              <Text>
                当サービスは来院日の
                <Text as="span" bold color="accent">
                  希望をリクエスト
                </Text>
                するものです。リクエスト送信時点では予約は確定しておらず、
                <Text bold as="span" color="accent">
                  病院とお電話で日程調整したあとで予約が成立
                </Text>
                します。状況によって希望日程を承れない場合もありますのでご了承ください。
              </Text>
            </Box>
          </Stack>
        )}

        <Box backgroundColor="primary" p="md" radius="sm" mt="md">
          <Stack spacing="xxs">
            <Text type="body" size="md">
              {medicalInstitution.abbreviatedName}
              {clinicalDepartmentNames.length > 0 && (
                <Text type="body" size="sm" as="span">
                  （{clinicalDepartmentNames.join('、')}）
                </Text>
              )}
            </Text>
            <Text type="body" size="sm">
              {address}
            </Text>
          </Stack>
        </Box>

        {renderMenus()}

        <Box mt="xl">
          <div className={styles.term}>
            <Box mb="sm">
              <Text color="sub" type="tag">
                <Link href={`${process.env.NEXT_PUBLIC_MEDICO_URL}/terms`}>利用規約</Link>と
                <Link href={`${process.env.NEXT_PUBLIC_MEDICO_URL}/policy`}>プライバシーポリシー</Link>
                に同意のうえご利用ください
              </Text>
            </Box>
          </div>

          {medicalInstitution.reservationPreference.isEnabled ? (
            <Button block onClick={_onNext} disabled={selectedMenuIndex == null}>
              次に進む
            </Button>
          ) : (
            <Box my="sm">
              <Text bold textAlign="center">
                現在、利用できません
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
